/// vue-i18n 9 Composition API setup.
/// Reference: evidence-srs.md Evidence #16.

import { createI18n } from 'vue-i18n'
import type { WritableComputedRef } from 'vue'

import jaAlert from './locales/ja/alert.json'
import jaAsset from './locales/ja/asset.json'
import jaAuth from './locales/ja/auth.json'
import jaCommon from './locales/ja/common.json'
import jaCompany from './locales/ja/company.json'
import jaCorpAdmin from './locales/ja/corp-admin.json'
import jaOrg from './locales/ja/org.json'
import jaScene from './locales/ja/scene.json'
import jaSettings from './locales/ja/settings.json'
import jaTtsScope from './locales/ja/tts-scope.json'
import enAlert from './locales/en/alert.json'
import enAsset from './locales/en/asset.json'
import enAuth from './locales/en/auth.json'
import enCommon from './locales/en/common.json'
import enCompany from './locales/en/company.json'
import enCorpAdmin from './locales/en/corp-admin.json'
import enOrg from './locales/en/org.json'
import enScene from './locales/en/scene.json'
import enSettings from './locales/en/settings.json'
import enTtsScope from './locales/en/tts-scope.json'
import jaTenantCorpAdmin from './locales/ja/tenant-corp-admin.json'
import enTenantCorpAdmin from './locales/en/tenant-corp-admin.json'
import jaZone from './locales/ja/zone.json'
import enZone from './locales/en/zone.json'
import jaTag from './locales/ja/tag.json'
import enTag from './locales/en/tag.json'
import jaMonitoring from './locales/ja/monitoring.json'
import enMonitoring from './locales/en/monitoring.json'
import jaDashboard from './locales/ja/dashboard.json'
import enDashboard from './locales/en/dashboard.json'
import jaCorpDashboard from './locales/ja/corp-dashboard.json'
import enCorpDashboard from './locales/en/corp-dashboard.json'
import jaNotification from './locales/ja/notification.json'
import enNotification from './locales/en/notification.json'
import jaImport from './locales/ja/import.json'
import enImport from './locales/en/import.json'
import jaFaq from './locales/ja/faq.json'
import jaGuide from './locales/ja/guide.json'
import enFaq from './locales/en/faq.json'
import enGuide from './locales/en/guide.json'
import jaAlertDiag from './locales/ja/alert-diagnostics.json'
import enAlertDiag from './locales/en/alert-diagnostics.json'
import jaUserPosHistory from './locales/ja/user-position-history.json'
import enUserPosHistory from './locales/en/user-position-history.json'
import jaAuditAction from './locales/ja/audit-action.json'
import enAuditAction from './locales/en/audit-action.json'

export type MessageSchema = typeof jaCommon &
  typeof jaAuth &
  typeof jaAlert &
  typeof jaAsset &
  typeof jaOrg &
  typeof jaSettings &
  typeof jaScene &
  typeof jaCompany &
  typeof jaCorpAdmin &
  typeof jaTtsScope &
  typeof jaTenantCorpAdmin &
  typeof jaZone &
  typeof jaTag &
  typeof jaMonitoring &
  typeof jaDashboard &
  typeof jaCorpDashboard &
  typeof jaNotification &
  typeof jaImport &
  typeof jaFaq &
  typeof jaGuide &
  typeof jaAlertDiag &
  typeof jaUserPosHistory &
  typeof jaAuditAction

const messages = {
  ja: { ...jaCommon, ...jaAuth, ...jaAlert, ...jaAsset, ...jaOrg, ...jaSettings, ...jaScene, ...jaCompany, ...jaCorpAdmin, ...jaTtsScope, ...jaTenantCorpAdmin, ...jaZone, ...jaTag, ...jaMonitoring, ...jaDashboard, ...jaCorpDashboard, ...jaNotification, ...jaImport, ...jaFaq, ...jaGuide, ...jaAlertDiag, ...jaUserPosHistory, ...jaAuditAction },
  en: { ...enCommon, ...enAuth, ...enAlert, ...enAsset, ...enOrg, ...enSettings, ...enScene, ...enCompany, ...enCorpAdmin, ...enTtsScope, ...enTenantCorpAdmin, ...enZone, ...enTag, ...enMonitoring, ...enDashboard, ...enCorpDashboard, ...enNotification, ...enImport, ...enFaq, ...enGuide, ...enAlertDiag, ...enUserPosHistory, ...enAuditAction },
}

export type AppLocale = 'ja' | 'en'

// User's locale choice survives reloads (and pre-login visits). After login the
// backend `users.locale` is the source of truth — auth-store re-applies it here.
const LOCALE_STORAGE_KEY = 'tts.locale'

function readPersistedLocale(): AppLocale | null {
  if (typeof localStorage === 'undefined') return null
  const value = localStorage.getItem(LOCALE_STORAGE_KEY)
  return value === 'ja' || value === 'en' ? value : null
}

export function persistLocale(locale: AppLocale): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  }
}

const defaultLocale =
  readPersistedLocale() ?? ((import.meta.env.VITE_DEFAULT_LOCALE ?? 'ja') as AppLocale)

export const i18n = createI18n<[MessageSchema], 'ja' | 'en'>({
  legacy: false, // Composition API mode (required for Vue 3)
  globalInjection: true,
  locale: defaultLocale,
  fallbackLocale: 'en',
  messages,
})

// createI18n's explicit-generics overload types `global.locale` as a plain
// string union, but at runtime (legacy: false → Composer) it is a writable ref.
const globalLocale = () =>
  (i18n.global as unknown as { locale: WritableComputedRef<AppLocale> }).locale

/** Switch the active UI locale (vue-i18n global). */
export function setI18nLocale(locale: AppLocale): void {
  globalLocale().value = locale
}

export function getI18nLocale(): AppLocale {
  return globalLocale().value
}
